import { Fragment, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financialsApi, manpowerApi, projectsApi, usersApi } from '@/lib/apiClient';
import { StateView } from '@/components';
import ActualByType, { actualByTypeCss } from './ActualByType';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import { asOfFromSearch, closedFinalityNote } from '@/lib/portfolioMonth';
import { combineRollUp } from '@/lib/rollUp';
import { computeRows, totals, summarize, groupRows, indirectOverrun, type GroupBy } from '@/lib/wip';
import { formatPeriod } from '@/lib/wipHistory';
import ForecastHistoryCard from './ForecastHistoryCard';
import GlDetailDrawer, { type GlDetailTarget } from './GlDetailDrawer';
import CtcBuildupDrawer from './CtcBuildupDrawer';

// Job Financials / WIP tab. Reads the raw facts and computes the WIP math
// client-side (lib/wip). Gated financials.view AND the showProjectCosts admin
// toggle. P3: PM forecast edit (CTC by cost code) + audited budget transfers +
// a WIP snapshot on save. CTC editing is keyed by cost code, so edit mode works
// in the Cost Code grouping; transfers persist immediately (audited).

const money = (v: number | null | undefined) => v == null ? '—' : '$' + Math.round(v).toLocaleString('en-US');
const moneyC = (v: number | null | undefined) => {
  if (v == null) return '—';
  const a = Math.abs(v), sign = v < 0 ? '-' : '';
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3)}K`;
  return `${sign}$${Math.round(a)}`;
};
const pct1 = (v: number) => `${(v * 100).toFixed(1)}%`;
const pts1 = (v: number) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)} pts`;
const parseNum = (s: string) => Number(String(s).replace(/[^0-9.\-]/g, ''));

const css = `
  .fin-prov { display:flex; align-items:center; gap:var(--ew-s-3); flex-wrap:wrap; background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:10px var(--ew-s-4); margin-bottom:var(--ew-s-4); }
  .fin-lock { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:#8A4E12; background:#FBEEE1; border-radius:3px; padding:4px 9px; }
  .fin-kpis { display:grid; grid-template-columns:repeat(auto-fit,minmax(180px,1fr)); gap:var(--ew-s-3); margin-bottom:var(--ew-s-4); }
  .fin-kpi { background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:14px var(--ew-s-4) 14px 18px; position:relative; overflow:hidden; }
  .fin-kpi::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--acc,var(--ew-n-300)); }
  .fin-kpi .l { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-n-500); }
  .fin-kpi .v { font:var(--ew-fw-bold) 26px/1.1 var(--ew-font); font-variant-numeric:tabular-nums; color:var(--ew-dark); margin-top:6px; }
  .fin-kpi .s { font:var(--ew-fw-regular) 12px var(--ew-font); color:var(--ew-n-500); margin-top:4px; }
  .fin-pill { display:inline-flex; align-items:center; gap:4px; font:var(--ew-fw-semibold) 11px/1 var(--ew-font); border-radius:3px; padding:3px 7px; margin-top:6px; width:fit-content; }
  .fin-mid { display:grid; grid-template-columns:1fr 1fr; gap:var(--ew-s-4); margin-bottom:var(--ew-s-4); }
  @media (max-width:900px){ .fin-mid { grid-template-columns:1fr; } }
  .fin-card { background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:var(--ew-s-4) var(--ew-s-5); }
  .fin-card h3 { font:var(--ew-fw-semibold) 14px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-dark); margin:0 0 var(--ew-s-4); }
  .fin-line { display:flex; justify-content:space-between; padding:6px 0; font-size:13px; border-bottom:1px solid #F2F2F1; }
  .fin-line.b { font-weight:600; }
  .fin-line .tnum { font-variant-numeric:tabular-nums; }
  .fin-bar { height:22px; border-radius:var(--ew-r-sm); background:var(--ew-n-100); position:relative; overflow:hidden; margin:8px 0; }
  .fin-bar > i { position:absolute; left:0; top:0; bottom:0; }
  .fin-callout { border-radius:var(--ew-r-md); padding:9px 12px; font-size:12.5px; line-height:1.45; margin-top:10px; }
  .fin-legend { display:flex; flex-wrap:wrap; gap:6px 16px; font-size:11.5px; color:#5A5A56; margin-top:8px; }
  .fin-legend span { display:inline-flex; align-items:center; gap:6px; }
  .fin-sw { width:11px; height:11px; border-radius:2px; }
  .fin-seg { display:inline-flex; gap:4px; }
  .fin-seg button { border:1px solid var(--ew-n-200); background:#fff; color:var(--ew-n-600); cursor:pointer; border-radius:var(--ew-r-md); padding:6px 14px; font:var(--ew-fw-medium) 12px/1 var(--ew-font); }
  .fin-seg button.active { background:var(--ew-dark); border-color:var(--ew-dark); color:#fff; }
  .fin-seg button:disabled { opacity:.4; cursor:not-allowed; }
  .fin-btn { font:var(--ew-fw-semibold) 12px/1 var(--ew-font); border-radius:var(--ew-r-md); padding:8px 14px; cursor:pointer; border:1px solid var(--ew-n-300); background:#fff; color:var(--ew-dark); }
  .fin-btn:hover:not(:disabled) { background:var(--ew-n-50); }
  .fin-btn.primary { background:var(--ew-green); border-color:var(--ew-green); color:#fff; }
  .fin-btn.primary:hover:not(:disabled) { background:var(--ew-green-700); }
  .fin-btn:disabled { opacity:.5; cursor:not-allowed; }
  .fin-tbl { width:100%; border-collapse:collapse; font-size:13px; }
  .fin-tbl th { text-align:right; padding:10px 12px; font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.05em; text-transform:uppercase; color:var(--ew-n-500); border-bottom:1px solid var(--ew-n-100); white-space:nowrap; }
  .fin-tbl th:first-child { text-align:left; }
  .fin-tbl td { padding:10px 12px; text-align:right; font-variant-numeric:tabular-nums; border-bottom:1px solid #F2F2F1; color:var(--ew-dark); vertical-align:top; }
  .fin-tbl td:first-child { text-align:left; }
  .fin-tbl tfoot td { font-weight:700; border-top:2px solid var(--ew-n-200); border-bottom:none; }
  .fin-tbl th.ctc-h, .fin-tbl td.ctc-c { background:var(--ctcbg,transparent); }
  .fin-ctc-input { width:110px; text-align:right; font-variant-numeric:tabular-nums; font-size:13px; padding:5px 7px; border:1px solid #D9C98F; border-radius:3px; background:#FFFDF5; font-family:inherit; }
  .fin-delta { display:inline-block; margin-top:4px; font:var(--ew-fw-semibold) 10px/1.5 var(--ew-font); border-radius:3px; padding:1px 6px; white-space:nowrap; }
  /* Deliberately quiet — it annotates the Actual, it isn't a warning. */
  .fin-indirect { margin-top:2px; font:var(--ew-fw-regular) 10.5px/1.4 var(--ew-font); color:var(--ew-n-500); white-space:nowrap; cursor:help; }
  .fin-drivers { background:#FBEEE1; border:1px solid #F0D9C0; border-radius:var(--ew-r-md); padding:9px 12px; margin-bottom:12px; font-size:12.5px; line-height:1.5; color:#8A4E12; display:flex; flex-wrap:wrap; align-items:center; gap:6px 10px; }
  .fin-chip { display:inline-flex; align-items:baseline; gap:5px; background:#fff; border:1px solid #F0D9C0; border-radius:var(--ew-r-pill); padding:2px 9px; font:var(--ew-fw-semibold) 11.5px/1.6 var(--ew-font); color:#B5611A; font-variant-numeric:tabular-nums; }
  .fin-chip small { color:var(--ew-n-500); font-weight:400; }
  .fin-tag { display:inline-block; margin-left:8px; padding:1px 6px; border-radius:var(--ew-r-sm); font:var(--ew-fw-semibold) 9.5px/1.6 var(--ew-font); letter-spacing:.04em; text-transform:uppercase; background:#FBEEE1; color:#8A4E12; border:1px solid #F0D9C0; vertical-align:middle; }
  .fin-tbl tr.over td { background:#FDF6EF; }
  /* Actual cells open the GL entries behind them. Dotted underline rather than a
     link colour — the whole column would read as navigation otherwise. */
  .fin-drill { border:none; background:none; padding:0; font:inherit; font-variant-numeric:tabular-nums; color:var(--ew-n-600); cursor:pointer; border-bottom:1px dotted var(--ew-n-400); }
  .fin-drill:hover { color:var(--ew-dark); border-bottom-color:var(--ew-dark); }
  /* Quiet by design: a hint under the CTC figure, not a call to action. */
  .fin-buildup { border:none; background:none; padding:0; margin-top:3px; cursor:pointer;
                 font:var(--ew-fw-regular) 10.5px/1.3 var(--ew-font); color:var(--ew-quality,#1F5E7A);
                 border-bottom:1px dotted currentColor; }
  .fin-buildup:empty { display:none; }
  .fin-editbanner { background:#FFFDF5; border:1px solid #E7D9A6; border-radius:var(--ew-r-md); padding:9px 12px; margin-bottom:12px; font-size:12.5px; line-height:1.45; color:#6B5D1F; display:flex; gap:8px; align-items:flex-start; }
  .fin-xfer { background:#F4F9F5; border:1px solid #CFE6D5; border-radius:var(--ew-r-md); padding:12px 14px; margin-bottom:12px; }
  .fin-xfer .xrow { display:flex; gap:12px; flex-wrap:wrap; align-items:flex-end; }
  .fin-xfer .fld { flex:1 1 200px; min-width:140px; }
  .fin-xfer label { display:block; font:var(--ew-fw-semibold) 10px/1.4 var(--ew-font); letter-spacing:.05em; text-transform:uppercase; color:var(--ew-n-600); margin-bottom:3px; }
  .fin-xfer select, .fin-xfer input { width:100%; font:inherit; font-size:13px; padding:6px 8px; border:1px solid var(--ew-n-300); border-radius:3px; background:#fff; font-family:inherit; box-sizing:border-box; }
  .fin-xfer .hint { font-size:11.5px; color:var(--ew-n-500); margin-top:8px; }
  .fin-log { margin-top:16px; }
  .fin-log h4 { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-n-500); margin:0 0 6px; }
  .fin-log-row { display:flex; gap:10px; flex-wrap:wrap; align-items:baseline; font-size:12.5px; padding:6px 0; border-bottom:1px solid #F2F2F1; }
  .fin-month { display:flex; align-items:center; gap:var(--ew-s-3); flex-wrap:wrap; margin-bottom:var(--ew-s-4); }
  .fin-month label { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-n-500); }
  .fin-month select { font:var(--ew-fw-medium) 13px var(--ew-font); padding:6px 10px; border:1px solid var(--ew-n-300); border-radius:var(--ew-r-md); background:#fff; color:var(--ew-dark); }
  .fin-scope { display:inline-flex; gap:4px; }
  .fin-scope button { border:1px solid var(--ew-n-200); background:#fff; color:var(--ew-n-600); cursor:pointer; border-radius:var(--ew-r-md); padding:6px 12px; font:var(--ew-fw-medium) 12px/1 var(--ew-font); }
  .fin-scope button.active { background:var(--ew-dark); border-color:var(--ew-dark); color:#fff; }
  .fin-rollsup { font-size:12.5px; color:var(--ew-n-500); }
  .fin-members { width:100%; border-collapse:collapse; margin-top:8px; font-size:12px; color:var(--ew-dark); background:#fff; border:1px solid #c9dff0; }
  .fin-members th { text-align:right; padding:5px 8px; font:var(--ew-fw-semibold) 10.5px/1 var(--ew-font); letter-spacing:.04em; text-transform:uppercase; color:var(--ew-n-500); border-bottom:1px solid #c9dff0; }
  .fin-members th:first-child, .fin-members td:first-child { text-align:left; }
  .fin-members td { padding:5px 8px; text-align:right; font-variant-numeric:tabular-nums; border-bottom:1px solid #eef3f8; }
  .fin-members tr.tot td { font-weight:700; border-top:2px solid #c9dff0; }
  .fin-pastbanner { background:#e7f0f8; border:1px solid #c9dff0; border-radius:var(--ew-r-md); padding:10px 14px; margin-bottom:var(--ew-s-4); font-size:12.5px; line-height:1.5; color:#15659E; }
  .fin-expand { border:none; background:none; cursor:pointer; padding:0 6px 0 0; margin-left:-2px; font:inherit; font-size:11px; color:var(--ew-n-500); }
  .fin-expand:hover { color:var(--ew-dark); }
  ${actualByTypeCss}
`;

/**
 * Keyed by job. The workspace reuses this component when you move from one job to
 * another on the Financials tab, so without the key the chosen month (and an edit in
 * progress, an open drawer) would follow you to a job that may have no such month: the
 * new job would be asked for the old job's September and show an error, or a history
 * view nobody picked. Keying here rather than at the call site covers every caller.
 */
export default function FinancialsTab({ projectId }: { projectId: number }) {
  return <FinancialsTabBody key={projectId} projectId={projectId} />;
}

// Read from the address bar rather than the router, so the tab needs no router to render.
const initialAsOf = (): string | null => (typeof window === 'undefined' ? null : asOfFromSearch(window.location.search));

function FinancialsTabBody({ projectId }: { projectId: number }) {
  const { data: config } = useQuery({ queryKey: ['manpower', 'config'], queryFn: manpowerApi.config, staleTime: 60_000 });
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60_000 });
  const showCosts = config?.showProjectCosts === '1';
  // null = the live tab. A 'YYYY-MM' is a past month, read-only (#613). It starts on the month
  // in `?asOf=` when there is one, so a link from the portfolio's month view lands on the same
  // month instead of today. Read once, at mount: choosing from the menu does not rewrite the URL.
  const [period, setPeriod] = useState<string | null>(initialAsOf);
  const viewingPast = period !== null;
  // A parent job can be shown COMBINED with the jobs that roll up to it (#613). Whether this job is
  // one is read from the roll-up family; a job in no family never offers the toggle, and before the
  // roll-up migration is applied nobody is a parent.
  const { data: family } = useQuery({
    queryKey: ['financials', projectId, 'family'],
    queryFn: () => financialsApi.family(projectId),
    enabled: showCosts,
    staleTime: 60_000,
  });
  const isParent = family?.role === 'parent';
  const [combined, setCombined] = useState(false);
  const combinedOn = combined && isParent;
  // While the family is on screen the single job's read is not made: a month the parent alone has
  // no freeze for would 404 under a view that is not asking for it.
  const q = useQuery({
    queryKey: period ? ['financials', projectId, 'asof', period] : ['financials', projectId],
    queryFn: () => (period ? financialsApi.wipAsOf(projectId, period) : financialsApi.wip(projectId)),
    enabled: showCosts && !combinedOn,
    staleTime: 60_000,
  });
  // The same view, for the family: each job's raw model, merged by the tab's own calc (lib/rollUp),
  // for today or for the month picked. `select` hands back the SAME shape this tab already renders.
  const rq = useQuery({
    queryKey: period ? ['financials', projectId, 'rollup', period] : ['financials', projectId, 'rollup'],
    queryFn: () => financialsApi.rollUp(projectId, period ?? undefined),
    select: combineRollUp,
    enabled: showCosts && combinedOn,
    staleTime: 60_000,
  });
  // Everything below reads `active`: the job alone, or the job combined with its family.
  const active = combinedOn ? rq : q;
  // The months worth opening: those the month-end freeze recorded for this job. Same
  // key as the Forecast History card, so the two share one fetch.
  const { data: history } = useQuery({
    queryKey: ['financials', projectId, 'history'],
    queryFn: () => financialsApi.history(projectId),
    enabled: showCosts,
    staleTime: 60_000,
  });
  // A closed month's row in the history is the closing snapshot, not the freeze.
  const months = useMemo(
    () => (history?.snapshots ?? []).filter((s) => s.source === 'month-end' || s.source === 'month-close')
      .map((s) => s.period).sort().reverse(),
    [history],
  );
  // Which months accounting has closed, to label the menu. Absent or failed = none
  // closed, which reads as "month end": the safe wording.
  const { data: periodList } = useQuery({
    queryKey: ['financials', 'periods'],
    queryFn: () => financialsApi.periods(),
    enabled: showCosts,
    staleTime: 60_000,
  });
  const closedMonths = useMemo(
    () => new Set((periodList?.periods ?? []).filter((p) => p.closed).map((p) => p.period)),
    [periodList],
  );
  const qc = useQueryClient();

  const isAdmin = me?.roles.includes('Admin') ?? false;
  // A past month is read-only: its forecast and budget are frozen, and a save would
  // stamp the CURRENT month, not the one on screen. So is a combination: a forecast is one
  // job's, and a save would have to say which of the jobs it was for.
  const readOnly = viewingPast || combinedOn;
  const canEdit = !readOnly && (isAdmin || (me?.permissions.includes('financials.edit_forecast') ?? false));
  const canTransfer = !readOnly && (isAdmin || (me?.permissions.includes('financials.transfer_budget') ?? false));

  const [group, setGroup] = useState<GroupBy>('type');
  const [editing, setEditing] = useState(false);
  const [ctcDraft, setCtcDraft] = useState<Record<string, string>>({});
  const [showTransfer, setShowTransfer] = useState(false);
  const [tFrom, setTFrom] = useState(''); const [tTo, setTTo] = useState('');
  const [tAmt, setTAmt] = useState(''); const [tReason, setTReason] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  // Which Actual figure the GL drill-down is open on (null = closed).
  const [glTarget, setGlTarget] = useState<GlDetailTarget | null>(null);
  // Which cost codes have their Actual-by-cost-type row open (#664). The entries come from the SAME query the
  // drawer uses, one fetch per job, and are only fetched once a row is opened.
  const [openTypes, setOpenTypes] = useState<ReadonlySet<string>>(() => new Set());
  // One id per code, and never the same id for two codes: a character outside [A-Za-z0-9-] becomes `_` plus its code point
  // in hex, so '01.02' and '01_02' differ ('_' is itself escaped).
  const typesRowId = (key: string) => `fin-bytype-${key.replace(/[^A-Za-z0-9-]/g, (c) => `_${c.charCodeAt(0).toString(16)}_`)}`;
  const toggleTypes = (key: string) => setOpenTypes((s) => { const n = new Set(s); if (!n.delete(key)) n.add(key); return n; });
  const glQ = useQuery({
    queryKey: ['financials', projectId, 'gl-detail'],
    queryFn: () => financialsApi.glDetail(projectId),
    enabled: showCosts && !combinedOn && openTypes.size > 0,
    staleTime: 60_000,
  });
  // Which cost code's CTC buildup is open (null = closed).
  const [buildup, setBuildup] = useState<string | null>(null);

  // Job number for the export filename only — the drawer works without it.
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{ jobNumber: string }>,
    staleTime: 60_000,
  });

  const saveMut = useMutation({
    mutationFn: (ctc: Record<string, number>) => financialsApi.saveForecast(projectId, ctc),
    onSuccess: (data) => {
      qc.setQueryData(['financials', projectId], data);
      // Saving freezes a snapshot server-side, so the history list is now stale.
      qc.invalidateQueries({ queryKey: ['financials', projectId, 'history'] });
      setEditing(false); setCtcDraft({}); setShowTransfer(false); setSavedFlash(true);
    },
  });
  const transferMut = useMutation({
    mutationFn: (dto: { fromCode: string; toCode: string; amount: number; reason?: string }) => financialsApi.transfer(projectId, dto),
    onSuccess: (data) => { qc.setQueryData(['financials', projectId], data); setTFrom(''); setTTo(''); setTAmt(''); setTReason(''); setShowTransfer(false); },
  });

  // Effective CTC = saved overrides, plus this session's typed edits (cleared
  // input removes the override → back to the default: remaining budget, or $0
  // once the job is Completed — see releaseRemaining in the model below).
  const effectiveCtc = useMemo(() => {
    const base = { ...(active.data?.forecast ?? {}) };
    if (!editing) return base;
    for (const [code, raw] of Object.entries(ctcDraft)) {
      const s = raw.trim();
      if (s === '') delete base[code];
      else { const n = parseNum(s); if (Number.isFinite(n)) base[code] = n; }
    }
    return base;
  }, [active.data, editing, ctcDraft]);

  const model = useMemo(() => {
    const data = active.data;
    if (!data) return null;
    // A Completed job's unspent budget is released — see ComputeOpts.releaseRemaining.
    const releaseRemaining = data.jobCompleted === true;
    const rows = computeRows(data.rows, { ctc: effectiveCtc, budgetAdj: data.budgetAdj, releaseRemaining });
    const t = totals(rows);
    const overBudget = rows.filter((r) => r.overUnder > 0).sort((a, b) => b.overUnder - a.overUnder);
    const unbudgeted = rows.filter((r) => !r.budgeted && ((r.actual ?? 0) > 0 || (r.committed ?? 0) > 0));
    const unbudgetedTotal = unbudgeted.reduce((sum, r) => sum + (r.actual ?? 0) + (r.committed ?? 0), 0);
    // Why some of those codes are over: see lib/wip indirectOverrun. Counts
    // POSITIVE indirect only — a credit reduces actual and can never be the
    // reason a code is over budget (raised in review on #278).
    const ind = indirectOverrun(overBudget);
    // Buildup lines grouped by cost code. A code present here has a read-only
    // CTC — the server derives it from these and ignores any typed override.
    const linesByCode = new Map<string, typeof data.ctcLines>();
    for (const l of data.ctcLines ?? []) {
      const list = linesByCode.get(l.costCode); if (list) list.push(l); else linesByCode.set(l.costCode, [l]);
    }
    // Saved CTC per code (no live draft) → the default value shown in each input.
    const savedRows = computeRows(data.rows, { ctc: data.forecast, budgetAdj: data.budgetAdj, releaseRemaining });
    const savedByCode: Record<string, number> = {};
    for (const r of savedRows) if (r.costCode) savedByCode[r.costCode] = r.ctc;
    const codeOptions = groupRows(savedRows, 'code')
      .filter((g) => g.key !== '—')
      .map((g) => ({ value: g.key, label: g.key + (g.sub ? ` — ${g.sub}` : '') }));
    return {
      rows, t, s: summarize(t, data.contract, data.indirectActual), grouped: groupRows(rows, group),
      prov: data.provenance, contract: data.contract, overBudget, unbudgetedTotal,
      ind, released: releaseRemaining,
      transferLog: data.transferLog, forecastStamp: data.forecastStamp, savedByCode, codeOptions, linesByCode,
    };
  }, [active.data, group, effectiveCtc]);

  if (!showCosts) {
    return <div style={{ padding: 'var(--ew-s-6)', color: 'var(--ew-n-500)', fontSize: 14 }}>Project cost visibility is turned off. An admin can enable it in Admin settings.</div>;
  }

  const startEdit = () => { setEditing(true); setGroup('code'); setSavedFlash(false); };
  const cancelEdit = () => { setEditing(false); setCtcDraft({}); setShowTransfer(false); setTFrom(''); setTTo(''); setTAmt(''); setTReason(''); };
  const saveForecast = () => {
    const eff = { ...(active.data?.forecast ?? {}) };
    for (const [code, raw] of Object.entries(ctcDraft)) {
      const s = raw.trim();
      if (s === '') delete eff[code];
      else { const n = parseNum(s); if (Number.isFinite(n)) eff[code] = n; }
    }
    saveMut.mutate(eff);
  };
  const applyTransfer = () => {
    const amount = parseNum(tAmt);
    if (!tFrom || !tTo || tFrom === tTo || !(amount > 0)) return;
    transferMut.mutate({ fromCode: tFrom, toCode: tTo, amount, reason: tReason || undefined });
  };
  const transferValid = !!tFrom && !!tTo && tFrom !== tTo && parseNum(tAmt) > 0;
  // Leaving the live tab abandons an edit in progress and closes whatever drawer is
  // open: both describe the view being left.
  const pickMonth = (value: string) => {
    cancelEdit(); setGlTarget(null); setBuildup(null); setSavedFlash(false);
    setPeriod(value === '' ? null : value);
  };
  // Same for the scope: a combination is read only, and its drawers are not this job's.
  const pickScope = (toCombined: boolean) => {
    cancelEdit(); setGlTarget(null); setBuildup(null); setSavedFlash(false);
    setCombined(toCombined);
  };

  return (
    <>
      <style>{css}</style>

      {/* ── Access / provenance ── */}
      <div className="fin-prov">
        <span className="fin-lock">🔒 Finance &amp; PM only</span>
        <span style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-quality)' }}>◇ Synced from Sage Intacct</span>
        {model?.prov.committedSyncedAt && !readOnly && <span style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>Last synced {fmtDate(model.prov.committedSyncedAt)}</span>}
        {model && !readOnly && (savedFlash || model.forecastStamp) && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: savedFlash ? 'var(--ew-green-700)' : 'var(--ew-n-500)', fontWeight: savedFlash ? 600 : 400 }}>
            {savedFlash
              ? '✓ Forecast saved · WIP snapshot taken'
              : `Forecast: ${fmtDate(model.forecastStamp!.asOf)}${model.forecastStamp!.by ? ` by ${model.forecastStamp!.by}` : ''}`}
          </span>
        )}
        {model && !model.prov.actualEnabled && (
          <span style={{ fontSize: 12, color: '#8A4E12', background: '#FBEEE1', borderRadius: 3, padding: '3px 8px' }}>◇ Actual cost illustrative — GL feed not enabled yet</span>
        )}
      </div>

      {/* ── Month: the live tab, or a past month as it stood at month end ── */}
      {(months.length > 0 || viewingPast || isParent || family?.role === 'child') && (
        <div className="fin-month">
          {(months.length > 0 || viewingPast) && (
            <>
              <label htmlFor="fin-month-select">View</label>
              <select id="fin-month-select" value={period ?? ''} onChange={(e) => pickMonth(e.target.value)}>
                <option value="">Current (live)</option>
                {/* Keep the open month listed even if the history list has not come back. */}
                {(period && !months.includes(period) ? [period, ...months] : months).map((p) => (
                  <option key={p} value={p}>{formatPeriod(p)} — {closedMonths.has(p) ? 'closed' : 'month end'}</option>
                ))}
              </select>
            </>
          )}
          {/* A parent offers its family combined; a child says where it rolls up to. */}
          {isParent && family && (
            <div className="fin-scope" role="group" aria-label="Scope">
              <button type="button" className={!combinedOn ? 'active' : ''} aria-pressed={!combinedOn} onClick={() => pickScope(false)}>This job only</button>
              <button type="button" className={combinedOn ? 'active' : ''} aria-pressed={combinedOn} onClick={() => pickScope(true)}>
                Combined with {family.children.length} job{family.children.length === 1 ? '' : 's'}
              </button>
            </div>
          )}
          {family?.role === 'child' && family.parent && (
            <span className="fin-rollsup">Rolls up to job {family.parent.jobNumber}. The combined figures are on that job&rsquo;s Financials tab.</span>
          )}
        </div>
      )}

      <StateView query={active} empty="No financial data for this job yet.">
        {() => {
          if (!model) return null;
          const { s, t, grouped, contract, overBudget, unbudgetedTotal, transferLog, savedByCode, codeOptions,
                  ind, linesByCode } = model;
          const buildupCodes = new Set(linesByCode.keys());
          const overbilled = s.overUnderBilled >= 0;
          // No estimate lines synced for this job at all. Distinct from a
          // genuinely zero budget: estimateProfit collapses to the full contract
          // value, so est. margin and fade would render as real-looking but
          // meaningless figures. Everything derived from the estimate is
          // suppressed below rather than shown against a phantom $0.
          const noEstimate = !model.prov.budgetSynced;
          const asOf = active.data?.asOf ?? null;
          // The family's own description, when this is the combination. It carries the month, who is
          // in it and who is not; the month banners below are the single job's and are not shown.
          const ru = combinedOn ? rq.data?.rollUp ?? null : null;
          // The web app and the API deploy separately. An API that predates the month
          // view ignores ?asOf and answers with the LIVE figures; showing those under a
          // month's name would be wrong, so a month with no `asOf` back is not shown.
          if (viewingPast && !asOf && !ru) {
            return (
              <div className="fin-callout" style={{ background: '#FBEEE1', color: '#8A4E12', border: '1px solid #F0D9C0', marginTop: 0 }}>
                <b>This month can’t be shown yet.</b> The server did not return it as of month end.
                Choose Current, or try again after the next update.
              </div>
            );
          }
          return (
            <>
              {/* What a past month shows, and which part of it can still move. The cost
                  half is read from Intacct as it stands today, so an entry accounting
                  posts later but dates inside the month still arrives; the contract,
                  estimate and POs are fixed at the moment the month was frozen. */}
              {ru && (() => {
                const jobs = ru.members.length;
                const recut = ru.asOf?.closed ? ru.members.filter((m) => m.final === false).length : 0;
                return (
                  <div className="fin-pastbanner" role="note">
                    <b>
                      Combined: job {ru.members[0].jobNumber} with the {jobs - 1} job{jobs - 1 === 1 ? '' : 's'} that roll{jobs - 1 === 1 ? 's' : ''} up to it. Read only.
                    </b>{' '}
                    {ru.asOf
                      ? <>As of the end of {formatPeriod(ru.asOf.period)}: cost is every entry dated through {fmtDate(ru.asOf.through)}; the contracts, estimates and purchase orders are as the month-end freezes recorded them. {ru.asOf.closed ? <>Closed{ru.asOf.closed.by ? ` by ${ru.asOf.closed.by}` : ''} on {fmtDateTime(ru.asOf.closed.at)}. {closedFinalityNote(jobs, recut)}</> : <>Accounting has not closed this month, so cost can still move.</>}</>
                      : <>Live figures.</>}{' '}
                    Contract, billed and every cost figure are the sum of the jobs below, each job&rsquo;s own cost to complete added up.
                    To forecast or transfer budget, switch to &ldquo;This job only&rdquo; and open that job.
                    {ru.missing.length > 0 && <> <b>Not in this view:</b> {ru.missing.map((m) => `${m.jobNumber} (${m.reason})`).join('; ')}.</>}
                    {ru.hidden > 0 && <> {ru.hidden} job{ru.hidden === 1 ? '' : 's'} you cannot see {ru.hidden === 1 ? 'is' : 'are'} not included.</>}
                    <table className="fin-members">
                      <thead><tr><th>Job</th><th>Contract</th><th>Billed</th><th>Cost to date</th><th>Projected profit</th></tr></thead>
                      <tbody>
                        {ru.members.map((m) => (
                          <tr key={m.projectId}>
                            <td>{m.jobNumber}{m.role === 'parent' ? ' (this job)' : ''} <span style={{ opacity: .75 }}>{m.title}</span>{ru.asOf?.closed && m.final === false ? ' · re-cut' : ''}{m.noEstimate ? ' · no estimate' : ''}{m.deleted ? ' · deleted since' : ''}</td>
                            <td>{money(m.summary.revised)}</td><td>{money(m.summary.billed)}</td><td>{money(m.actual)}</td><td>{money(m.summary.projectedProfit)}</td>
                          </tr>
                        ))}
                        <tr className="tot">
                          <td>Combined</td>
                          <td>{money(s.revised)}</td><td>{money(s.billed)}</td><td>{money(t.actual)}</td><td>{money(s.projectedProfit)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              {asOf && !asOf.closed && (
                <div className="fin-pastbanner" role="note">
                  <b>{formatPeriod(asOf.period)} as of month end — read only.</b>{' '}
                  Cost is every Intacct entry dated through {fmtDate(asOf.through)}, so an entry posted
                  later but dated in the month is included. The contract, estimate and purchase orders
                  are as they stood when the month was frozen ({fmtDateTime(asOf.frozenAt)}); a change
                  entered after month end is not in this view. Accounting has not closed this month, so
                  cost can still move. To forecast or transfer budget, switch back to Current.
                </div>
              )}
              {asOf?.closed && (
                <div className="fin-pastbanner" role="note">
                  <b>{formatPeriod(asOf.period)} — closed{asOf.closed.by ? ` by ${asOf.closed.by}` : ''} on {fmtDateTime(asOf.closed.at)}. Read only.</b>{' '}
                  These are the final figures, stored when the month was closed: they no longer change
                  if Intacct does. Cost is every entry dated through {fmtDate(asOf.through)}; the
                  contract, estimate and purchase orders are as they stood at month end. To forecast or
                  transfer budget, switch back to Current.
                </div>
              )}
              {noEstimate && (
                <div className="fin-callout" style={{ background: '#FBEEE1', color: '#8A4E12', border: '1px solid #F0D9C0', marginTop: 0, marginBottom: 'var(--ew-s-4)' }}>
                  <b>{ru ? '⚠ A job in this combination has no Sage estimate synced.' : '⚠ No Sage estimate synced for this job.'}</b> Budget, estimate margin and fade
                  are unavailable — they are not zero, they are missing, so anything measured
                  against the estimate is hidden rather than shown as $0. Actual cost, committed
                  cost and the contract are unaffected and remain accurate. If {ru ? 'that job' : 'the job'} has an
                  estimate in Sage Intacct, check it is flagged as the <b>primary</b> estimate —
                  only the primary estimate is pulled.
                </div>
              )}
              {/* Why a finished job's forecast reads $0: without this the default looks
                  like a missing forecast rather than a rule. Informational, not a warning. */}
              {model.released && (
                <div className="fin-callout" style={{ background: '#e7f0f8', color: '#15659E', marginTop: 0, marginBottom: 'var(--ew-s-4)' }}>
                  <b>Completed in Sage Intacct — budget left on each cost code is released.</b> A code
                  with no forecast of its own has $0 cost to complete, so the projected margin is the
                  contract less what the job has actually cost. A cost-to-complete you enter, or build up, still counts.
                </div>
              )}
              {/* ── KPI band ── */}
              <div className="fin-kpis">
                <div className="fin-kpi" style={{ ['--acc' as any]: 'var(--ew-performance)' }}>
                  <div className="l">Projected Margin</div>
                  <div className="v">{moneyC(s.projectedProfit)} <span style={{ fontSize: 15, color: 'var(--ew-n-500)' }}>{pct1(s.projectedMarginPct)}</span></div>
                  {/* Fade and the estimate comparison are only meaningful against a
                      real estimate. With none synced, estimateProfit collapses to the
                      whole contract value — est. margin reads ~100% and fade looks
                      catastrophic. Suppress rather than display nonsense. */}
                  {noEstimate ? (
                    <div className="s" style={{ color: '#8A4E12' }}>No Sage estimate synced — margin vs estimate unavailable</div>
                  ) : (<>
                    <span className="fin-pill" style={{ background: s.fadeDollars < 0 ? '#FBEEE1' : 'var(--ew-green-50)', color: s.fadeDollars < 0 ? '#8A4E12' : 'var(--ew-green-700)' }}>
                      {s.fadeDollars < 0 ? '▼' : '▲'} {pts1(s.fadePts)} fade
                    </span>
                    <div className="s">vs {pct1(s.estimateMarginPct)} est. margin</div>
                  </>)}
                </div>
                <div className="fin-kpi" style={{ ['--acc' as any]: 'var(--ew-green)' }}>
                  <div className="l">Super Margin</div>
                  <div className="v">{moneyC(s.superProfit)} <span style={{ fontSize: 15, color: 'var(--ew-n-500)' }}>{pct1(s.superMarginPct)}</span></div>
                  <div className="s">before indirects · {pct1(s.projectedMarginPct)} after</div>
                </div>
                <div className="fin-kpi" style={{ ['--acc' as any]: 'var(--ew-quality)' }}>
                  <div className="l">Billing Position</div>
                  <div className="v">{moneyC(Math.abs(s.overUnderBilled))} <span style={{ fontSize: 13, color: 'var(--ew-quality)', fontWeight: 600 }}>{overbilled ? 'Overbilled' : 'Underbilled'}</span></div>
                  <div className="s">Billed {moneyC(s.billed)} · Earned {moneyC(s.earned)}</div>
                </div>
                <div className="fin-kpi" style={{ ['--acc' as any]: s.costVariance > 0 ? 'var(--ew-performance)' : 'var(--ew-green)' }}>
                  <div className="l">Cost vs Budget</div>
                  <div className="v" style={{ color: s.costVariance > 0 ? '#B5611A' : 'var(--ew-green-700)' }}>{s.costVariance >= 0 ? '+' : ''}{moneyC(s.costVariance)}</div>
                  <div className="s">EAC {moneyC(t.eac)} vs {moneyC(t.budget)}</div>
                </div>
                <div className="fin-kpi" style={{ ['--acc' as any]: 'var(--ew-dark)' }}>
                  <div className="l">Left to Bill</div>
                  <div className="v">{moneyC(s.leftToBill)}</div>
                  <div className="s">of {moneyC(s.revised)} revised contract</div>
                </div>
              </div>

              {/* ── Mid: Contract & Billing / Cost & Margin ── */}
              <div className="fin-mid">
                <div className="fin-card">
                  <h3>Contract &amp; Billing</h3>
                  <div className="fin-line"><span>Original contract</span><span className="tnum">{money(contract?.original)}</span></div>
                  <div className="fin-line"><span>+ Approved change orders</span><span className="tnum" style={{ color: 'var(--ew-green-700)' }}>{contract?.approvedChange ? '+' : ''}{money(contract?.approvedChange)}</span></div>
                  <div className="fin-line b"><span>Revised contract</span><span className="tnum">{money(contract?.revised)}</span></div>
                  <div className="fin-line" style={{ color: 'var(--ew-n-500)' }}><span>Pending change orders (not in contract)</span><span className="tnum">{money(contract?.pendingChange)}</span></div>
                  <div style={{ marginTop: 14, fontSize: 12, fontWeight: 600 }}>Billed vs earned</div>
                  <div className="fin-bar">
                    <i style={{ width: `${clampPct(s.revised ? s.billed / s.revised : 0)}%`, background: 'var(--ew-quality)' }} />
                    <i style={{ left: `${clampPct(s.revised ? s.earned / s.revised : 0)}%`, width: 2, background: 'var(--ew-dark)' }} />
                  </div>
                  <div className="fin-legend">
                    <span><span className="fin-sw" style={{ background: 'var(--ew-quality)' }} />Billed <b>{moneyC(s.billed)}</b></span>
                    <span><span className="fin-sw" style={{ background: 'var(--ew-dark)', width: 11, height: 2 }} />Earned <b>{moneyC(s.earned)}</b></span>
                  </div>
                  <div className="fin-callout" style={{ background: overbilled ? '#e7f0f8' : '#FBEEE1', color: overbilled ? '#15659E' : '#8A4E12' }}>
                    {overbilled
                      ? `Overbilled ${moneyC(s.overUnderBilled)} — billing is running ahead of earned cost.`
                      : `Underbilled ${moneyC(-s.overUnderBilled)} — earned cost is ahead of billing; catch up on the schedule of values.`}
                  </div>
                </div>

                <div className="fin-card">
                  <h3>Cost &amp; Margin</h3>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ew-n-600)' }}>Cost complete</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 2 }}>
                      <span style={{ font: 'var(--ew-fw-bold) 22px/1 var(--ew-font)' }}>{pct1(s.pctComplete)}</span>
                      <span style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>of projected final cost (actual ÷ EAC)</span>
                    </div>
                  </div>
                  <div className="fin-bar">
                    <i style={{ width: `${clampPct(t.eac ? t.actual / Math.max(t.eac, t.budget) : 0)}%`, background: 'var(--ew-dark)' }} />
                    <i style={{ left: `${clampPct(t.actual / Math.max(t.eac, t.budget || 1))}%`, width: `${clampPct(t.ctc / Math.max(t.eac, t.budget || 1))}%`, background: 'repeating-linear-gradient(135deg,#CDE0D2 0 4px,#C2D8C8 4px 8px)' }} />
                    <i style={{ left: `${clampPct(t.budget / Math.max(t.eac, t.budget || 1))}%`, width: 2, background: 'var(--ew-danger)' }} title="Budget" />
                  </div>
                  <div className="fin-legend">
                    <span><span className="fin-sw" style={{ background: 'var(--ew-dark)' }} />Actual <b>{moneyC(t.actual)}</b></span>
                    <span><span className="fin-sw" style={{ background: '#CDE0D2' }} />To complete <b>{moneyC(t.ctc)}</b></span>
                    <span><span className="fin-sw" style={{ background: 'var(--ew-danger)', width: 3 }} />Budget {moneyC(t.budget)}</span>
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <div className="fin-line"><span>EAC — projected final cost <span style={{ color: 'var(--ew-quality)' }}>△</span></span><span className="tnum" style={{ fontWeight: 600 }}>{money(t.eac)}</span></div>
                    <div className="fin-line"><span>Revised est. profit</span><span className="tnum">{noEstimate ? <span style={{ color: 'var(--ew-n-400)' }}>no estimate synced</span> : <>{money(s.estimateProfit)} <span style={{ color: 'var(--ew-n-500)' }}>{pct1(s.estimateMarginPct)}</span></>}</span></div>
                    <div className="fin-line b"><span>Projected profit</span><span className="tnum">{money(s.projectedProfit)} <span style={{ color: 'var(--ew-n-500)' }}>{pct1(s.projectedMarginPct)}</span></span></div>
                    {/* The indirect burden on its own line, between the two
                        margins, so the arithmetic reads instead of being
                        implied: projected profit + indirects = super margin.
                        Finance asked to see the figure itself, not only its
                        effect (product owner, 2026-07-28). */}
                    <div className="fin-line" style={{ color: 'var(--ew-n-500)' }}>
                      <span>
                        Allocated indirects added back{' '}
                        <span title="Indirect cost allocated to this job in the GL (account 59998). It sits inside actual cost and EAC; Super Margin carves it back out." style={{ color: 'var(--ew-quality)' }}>△</span>
                      </span>
                      <span className="tnum">
                        {s.indirectActual === 0
                          ? <span style={{ color: 'var(--ew-n-400)' }}>none allocated yet</span>
                          : `+ ${money(s.indirectActual)}`}
                      </span>
                    </div>
                    <div className="fin-line" style={{ color: 'var(--ew-green-700)' }}><span>Super margin — before indirects <span title="Project margin before job-allocated indirect costs (GL 59998), added back to projected profit" style={{ color: 'var(--ew-quality)' }}>△</span></span><span className="tnum">{money(s.superProfit)} <span style={{ color: 'var(--ew-n-500)' }}>{pct1(s.superMarginPct)}</span></span></div>
                    <div className="fin-line b" style={{ color: noEstimate ? 'var(--ew-n-400)' : (s.fadeDollars < 0 ? '#B5611A' : 'var(--ew-green-700)') }}><span>Fade vs revised estimate</span><span className="tnum">{noEstimate ? '—' : <>{moneyC(s.fadeDollars)} · {pts1(s.fadePts)}</>}</span></div>
                  </div>
                  {s.revised > 0 && s.budget > s.revised && (
                    <div className="fin-callout" style={{ background: '#FBEEE1', color: '#8A4E12', border: '1px solid #F0D9C0', marginTop: 12 }}>
                      <b>⚠ Estimate exceeds contract by {moneyC(s.budget - s.revised)}</b> — a baseline issue to reconcile, not a cost overrun. The Sage estimate ({moneyC(s.budget)}) is above the revised contract ({moneyC(s.revised)}), so the est. margin reads negative on its own. Reconcile in Sage — the bid may not be re-baselined to the awarded/VE&apos;d contract, or approved change orders are missing from PROJECTCONTRACT{canEdit ? ' — or use Update Forecast to set a realistic cost-to-complete' : ''}.
                    </div>
                  )}
                </div>
              </div>

              {/* ── Job Cost Detail ── */}
              <div className="fin-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                  <h3 style={{ margin: 0 }}>Job Cost Detail</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {editing && canTransfer && (
                      <button className="fin-btn" onClick={() => setShowTransfer((v) => !v)}>⇄ Transfer Budget</button>
                    )}
                    <div className="fin-seg">
                      <button className={group === 'type' ? 'active' : ''} disabled={editing} onClick={() => setGroup('type')}>Cost Type</button>
                      <button className={group === 'code' ? 'active' : ''} disabled={editing} onClick={() => setGroup('code')}>Cost Code</button>
                    </div>
                    {!editing && canEdit && <button className="fin-btn" onClick={startEdit}>✎ Update Forecast</button>}
                    {editing && (
                      <>
                        <button className="fin-btn" onClick={cancelEdit} disabled={saveMut.isPending}>Cancel</button>
                        <button className="fin-btn primary" onClick={saveForecast} disabled={saveMut.isPending}>{saveMut.isPending ? 'Saving…' : 'Save Forecast'}</button>
                      </>
                    )}
                  </div>
                </div>

                {editing && (
                  <div className="fin-editbanner">
                    <span>✎</span>
                    <span>Type a <b>cost-to-complete</b> for any cost code — EAC, margin and fade recompute live. Editing is by cost code. <b>Transfer Budget</b> moves budget between codes (job total held constant, audited). <b>Save Forecast</b> writes the forecast and takes a WIP snapshot; Cancel discards unsaved cost-to-complete edits.</span>
                  </div>
                )}

                {editing && showTransfer && canTransfer && (
                  <div className="fin-xfer">
                    <div className="xrow">
                      <div className="fld">
                        <label>From cost code</label>
                        <select value={tFrom} onChange={(e) => setTFrom(e.target.value)}>
                          <option value="">Select cost code…</option>
                          {codeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      <div className="fld">
                        <label>To cost code</label>
                        <select value={tTo} onChange={(e) => setTTo(e.target.value)}>
                          <option value="">Select cost code…</option>
                          {codeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      <div className="fld" style={{ flex: '0 1 150px' }}>
                        <label>Amount</label>
                        <input inputMode="decimal" placeholder="0" value={tAmt} onChange={(e) => setTAmt(e.target.value)} />
                      </div>
                      <div className="fld" style={{ flex: '2 1 240px' }}>
                        <label>Reason</label>
                        <input placeholder="e.g. cover formwork overrun from earthwork underrun" value={tReason} onChange={(e) => setTReason(e.target.value)} />
                      </div>
                      <button className="fin-btn primary" onClick={applyTransfer} disabled={!transferValid || transferMut.isPending}>{transferMut.isPending ? 'Applying…' : 'Apply'}</button>
                    </div>
                    <div className="hint">Moves budget between codes · job total held constant · audited{transferMut.isError ? ' · transfer failed, try again' : ''}</div>
                  </div>
                )}

                {(overBudget.length > 0 || unbudgetedTotal > 0) && (
                  <div className="fin-drivers">
                    {overBudget.length > 0 && (
                      <span><b>▲ {overBudget.length} cost {overBudget.length === 1 ? 'code' : 'codes'} over budget</b> — {moneyC(t.overUnder)} projected past budget:</span>
                    )}
                    {overBudget.slice(0, 5).map((r) => (
                      <span key={r.costCode ?? '—'} className="fin-chip" title={r.costCodeName ?? undefined}>
                        {r.costCode ?? '—'} +{moneyC(r.overUnder)}{!r.budgeted ? <small>· unbudgeted</small> : null}
                      </span>
                    ))}
                    {overBudget.length > 5 && <span>+{overBudget.length - 5} more</span>}
                    {unbudgetedTotal > 0 && (
                      <span style={{ width: '100%', color: '#8A4E12' }}>
                        {moneyC(unbudgetedTotal)} of cost sits on codes the estimate never carried (shown as <span className="fin-tag">Unbudgeted</span> below).
                      </span>
                    )}
                    {/* The allocated-indirect explanation, placed here rather
                        than as a page note: this is the moment a PM asks "why
                        is this code over?", and for some codes the honest
                        answer is "it isn't, really". Finance confirmed the
                        treatment matches Intacct (2026-07-31) —
                        the figures are right, they just needed explaining. */}
                    {ind.codes.length > 0 && (
                      <span style={{ width: '100%', color: '#8A4E12' }}>
                        {moneyC(ind.total)} of the cost on {ind.codes.length === 1 ? 'that code' : `${ind.codes.length} of those codes`} is
                        {' '}<b>allocated indirect</b>, which the estimate carries no budget for.
                        {ind.onlyFromIndirect.length > 0 && (
                          <> {ind.onlyFromIndirect.length === 1
                            ? <>Code <b>{ind.onlyFromIndirect[0].costCode ?? '—'}</b> is over budget for that reason alone.</>
                            : <><b>{ind.onlyFromIndirect.length}</b> of them are over budget for that reason alone.</>}</>
                        )}
                        {' '}Intacct reports cost the same way, so this is expected rather than an error.
                      </span>
                    )}
                  </div>
                )}
                <div className="ew-table-scroll">
                  <table className="fin-tbl">
                    <thead>
                      <tr>
                        <th>{group === 'type' ? 'Cost Type' : 'Cost Code'}</th>
                        <th>Budget</th><th>Committed ●</th><th>Actual ◇</th>
                        <th className="ctc-h" style={{ ['--ctcbg' as any]: editing ? '#FFFDF5' : 'transparent' }}>To Complete △</th>
                        <th>EAC</th><th>Over/(Under)</th><th>% Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped.map((g) => {
                        const editable = editing && group === 'code' && g.key !== '—';
                        // The Actual-by-cost-type row (#664): by cost code only, never while editing the forecast,
                        // and not on a combination (its entries are several jobs' and the read is one job's).
                        const canExpand = group === 'code' && !editing && !ru;
                        const typesOpen = canExpand && openTypes.has(g.key);
                        return (
                          <Fragment key={g.key}>
                          <tr className={g.overUnder > 0 ? 'over' : undefined}>
                            <td>
                              {canExpand && (
                                <button
                                  type="button"
                                  className="fin-expand"
                                  aria-expanded={typesOpen}
                                  aria-controls={typesOpen ? typesRowId(g.key) : undefined}
                                  aria-label={`${typesOpen ? 'Hide' : 'Show'} actual cost by cost type for ${g.label}`}
                                  title="Actual cost by cost type"
                                  onClick={() => toggleTypes(g.key)}
                                >
                                  {typesOpen ? '▾' : '▸'}
                                </button>
                              )}
                              <span style={{ fontWeight: 600 }}>{g.label}</span>
                              {g.sub ? <span style={{ color: 'var(--ew-n-500)', fontWeight: 400 }}> · {g.sub}</span> : null}
                              {g.unbudgeted ? <span className="fin-tag">Unbudgeted</span> : null}
                            </td>
                            <td style={g.budgetDelta > 0 ? { background: '#EFF5FB', boxShadow: 'inset 3px 0 0 #1875BD', fontWeight: 700 } : g.budgetDelta < 0 ? { background: '#FCF2EA', boxShadow: 'inset 3px 0 0 #DB7A25', fontWeight: 700 } : undefined}>
                              {money(g.budget)}
                              {g.budgetDelta !== 0 && (
                                <div className="fin-delta" style={g.budgetDelta > 0 ? { color: '#15659E', background: '#dbe9f5' } : { color: '#B5611A', background: '#F7DFC9' }}>
                                  {g.budgetDelta > 0 ? '▲ +' : '▼ −'}{moneyC(Math.abs(g.budgetDelta))} · was {money(g.budgetBase)}
                                </div>
                              )}
                            </td>
                            <td style={{ color: 'var(--ew-n-500)' }}>{g.committed ? money(g.committed) : '—'}</td>
                            <td>
                              {/* Always drillable, INCLUDING at $0. A zero Actual
                                  does not mean there is nothing behind it — a
                                  posted charge and its reversal net to exactly
                                  zero, and those entries are precisely what
                                  finance needs to see. Suppressing the button on
                                  `actual === 0` hid them. A code with genuinely no
                                  entries gets the drawer's empty state, which is
                                  accurate rather than a dead end. */}
                              {/* A combination has no drill-down: the entries behind it are several
                                  jobs' and the drawer reads one. Each job's own tab has it. */}
                              {ru
                                ? <span>{money(g.actual)}</span>
                                : (
                                  <button
                                    className="fin-drill"
                                    title={`Show the GL entries behind ${money(g.actual)}`}
                                    onClick={() => setGlTarget({ label: g.label, sub: g.sub, codes: g.codes, expected: g.actual })}
                                  >
                                    {money(g.actual)}
                                  </button>
                                )}
                              {/* Why this code can read above budget. The estimate
                                  budgets nothing to allocated indirect, so any of
                                  it landing here has no budget to sit against.
                                  Finance confirmed the treatment matches Intacct
                                  (2026-07-31) — so this explains the number, it
                                  does not correct it. Shown only when there is
                                  some, or every row grows a redundant line. */}
                              {g.indirect !== 0 && (
                                <div
                                  className="fin-indirect"
                                  title={g.indirect > 0
                                    ? 'Allocated indirect (GL 59998) included in this Actual. The estimate carries no budget for it, so it pushes the code above budget. This matches how Intacct reports cost.'
                                    : 'A net allocated-indirect CREDIT (GL 59998) is included in this Actual, reducing it.'}
                                >
                                  {/* A credit is worded as one rather than rendered as a
                                      negative: "incl. -$500 allocated indirect" reads as
                                      an error, and it is the opposite of the overrun
                                      story the positive case tells. */}
                                  incl. {moneyC(Math.abs(g.indirect))} allocated indirect{g.indirect < 0 ? ' credit' : ''}
                                </div>
                              )}
                            </td>
                            <td className="ctc-c" style={{ ['--ctcbg' as any]: editing ? '#FFFDF5' : 'transparent', color: g.ctcOverridden && !ru ? 'var(--ew-quality)' : 'var(--ew-n-500)', fontWeight: g.ctcOverridden && !ru ? 600 : 400 }}>
                              {/* A built-up code is NOT typeable: its CTC is the
                                  sum of its lines. Offering an input would let a
                                  PM change a number the server then ignores. The
                                  buildup button opens the workings instead. */}
                              {editable && !buildupCodes.has(g.key)
                                ? <input className="fin-ctc-input" inputMode="decimal"
                                    value={ctcDraft[g.key] ?? String(Math.round(savedByCode[g.key] ?? g.ctc))}
                                    onChange={(e) => setCtcDraft((d) => ({ ...d, [g.key]: e.target.value }))} />
                                : money(g.ctc)}
                              {group === 'code' && g.key !== '—' && (
                                <div>
                                  <button className="fin-buildup" onClick={() => setBuildup(g.key)}
                                          title={buildupCodes.has(g.key)
                                            ? 'Cost to complete is built up from lines — open to review or edit'
                                            : 'Build this cost to complete up from crew, units or a lump sum'}>
                                    {buildupCodes.has(g.key)
                                      ? `${linesByCode.get(g.key)!.length} line${linesByCode.get(g.key)!.length === 1 ? '' : 's'}`
                                      : (canEdit ? 'build up' : '')}
                                  </button>
                                </div>
                              )}
                            </td>
                            <td style={{ fontWeight: 600 }}>{money(g.eac)}</td>
                            <td style={{ color: g.overUnder > 0 ? '#B5611A' : 'var(--ew-green-700)' }}>{g.overUnder >= 0 ? '' : '('}{money(Math.abs(g.overUnder))}{g.overUnder >= 0 ? '' : ')'}</td>
                            <td style={{ color: 'var(--ew-n-500)' }}>{g.costPct.toFixed(0)}%</td>
                          </tr>
                          {typesOpen && (
                            <ActualByType
                              id={typesRowId(g.key)} entries={glQ.data?.lines} failed={glQ.isError} codes={g.codes}
                              through={asOf?.through ?? null} closed={asOf?.closed != null} expected={g.actual} colSpan={8}
                            />
                          )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td>{money(t.budget)}</td><td>{money(t.committed)}</td>
                        <td>
                          {/* Same at job level: a job whose costs net to zero
                              still has entries worth reading. */}
                          {ru
                            ? <span style={{ fontWeight: 700 }}>{money(t.actual)}</span>
                            : (
                              <button
                                className="fin-drill"
                                style={{ fontWeight: 700 }}
                                title="Show every GL entry on this job"
                                onClick={() => setGlTarget({ label: 'Whole job', sub: null, codes: null, expected: t.actual })}
                              >
                                {money(t.actual)}
                              </button>
                            )}
                        </td>
                        <td>{money(t.ctc)}</td><td>{money(t.eac)}</td>
                        <td style={{ color: t.overUnder > 0 ? '#B5611A' : 'var(--ew-green-700)' }}>{t.overUnder >= 0 ? '' : '('}{money(Math.abs(t.overUnder))}{t.overUnder >= 0 ? '' : ')'}</td>
                        <td>{(t.eac ? (t.actual / t.eac) * 100 : 0).toFixed(0)}%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="fin-legend" style={{ marginTop: 12 }}>
                  <span><span style={{ color: 'var(--ew-quality)', fontWeight: 700 }}>●</span> Live from Sage Intacct — committed, contract &amp; COs, billed</span>
                  <span><span style={{ color: 'var(--ew-n-500)', fontWeight: 700 }}>◇</span> Actual cost from the GL feed{!model.prov.actualEnabled ? ' (illustrative until enabled)' : ''} — <b>click any Actual figure</b> for the entries behind it</span>
                  <span><span style={{ color: 'var(--ew-quality)', fontWeight: 700 }}>△</span> Forecast — cost-to-complete &amp; EAC (PM override where set, else {model.released ? '$0 — the job is Completed' : 'remaining budget'})</span>
                </div>

                {transferLog.length > 0 && (
                  <div className="fin-log">
                    <h4>Budget Transfer Log</h4>
                    {transferLog.map((x) => (
                      <div key={x.id} className="fin-log-row">
                        <b style={{ color: 'var(--ew-dark)' }}>{money(x.amount)}</b>
                        <span style={{ color: 'var(--ew-n-600)' }}>{x.fromCode} → {x.toCode}</span>
                        <span style={{ color: 'var(--ew-n-500)', flex: '1 1 200px' }}>{x.reason || '—'}</span>
                        <span style={{ color: 'var(--ew-n-400)', fontSize: 11.5 }}>{x.by ? `${x.by} · ` : ''}{fmtDate(x.at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Frozen monthly forecasts. Reads the same snapshots the forecast
                  save has been writing since 2026-07-21 — until now nothing
                  displayed them. */}
              {/* Not on a past month: it compares frozen months with the LIVE figures,
                  which would sit beside a view that is not live. */}
              {!readOnly && <ForecastHistoryCard projectId={projectId} liveRows={model.rows} liveLabel="live" />}
            </>
          );
        }}
      </StateView>

      {glTarget && (
        <GlDetailDrawer
          projectId={projectId}
          jobNumber={project?.jobNumber ?? null}
          target={glTarget}
          through={active.data?.asOf?.through ?? null}
          onClose={() => setGlTarget(null)}
        />
      )}
      {/* CTC buildup for one cost code. Reads its context (budget / actual /
          committed) off the already-computed rows so the drawer can't disagree
          with the table that opened it. */}
      {buildup && model && (() => {
        const g = model.grouped.find((x) => x.key === buildup)
          ?? { budget: 0, actual: 0, committed: 0, sub: null } as any;
        const row = model.rows.find((r) => r.costCode === buildup);
        return (
          <CtcBuildupDrawer
            projectId={projectId}
            costCode={buildup}
            costTypeGroup={row?.costTypeGroup ?? 'Other'}
            costCodeName={g.sub ?? null}
            budget={g.budget}
            actual={g.actual}
            committed={g.committed}
            lines={model.linesByCode.get(buildup) ?? []}
            canEdit={canEdit}
            onClose={() => setBuildup(null)}
            onSaved={() => {
              // The CTC, EAC, margin and fade all move with the buildup, so the
              // whole model refetches rather than patching the one cell. Saving
              // a buildup also freezes a WIP snapshot server-side, exactly as
              // the typed forecast does, so Forecast History has to refetch too
              // or it keeps showing the previous figures. The key is a prefix of
              // the history key, so this invalidates both.
              qc.invalidateQueries({ queryKey: ['financials', projectId] });
            }}
          />
        );
      })()}
    </>
  );
}

function clampPct(frac: number): number {
  return Math.max(0, Math.min(100, frac * 100));
}
